/**
 * Atlas — Login, Register, Forgot Password, Reset Password pages.
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI, marketplaceAPI } from '../../services/api';
import { CONSENT_VERSION } from '../common/ConsentBanner';
import DisclaimerFooter from '../common/DisclaimerFooter';

// ── Shared auth page shell (centers content, footer at bottom) ──────────────
function AuthPageShell({ children, maxWidth = 420 }) {
  return (
    <div className="auth-outer">
      <div className="auth-inner">
        <div style={{ width: '100%', maxWidth }}>
          {children}
        </div>
      </div>
      <DisclaimerFooter />
    </div>
  );
}

// ── Shared brand header ─────────────────────────────────────────────────────
function BrandHeader({ subtitle }) {
  return (
    <div style={{ textAlign: 'center', marginBottom: 32 }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <img src="/brand/svg/propatlas-mark.svg" className="brand-mark" alt="PropAtlas mark" style={{ width: 34 }} />
        <span className="brand-name" style={{ fontSize: 28 }}>Prop<em>A</em>tlas</span>
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>{subtitle}</div>
    </div>
  );
}

function extractError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || 'Something went wrong';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    return first?.msg || first?.message || 'Invalid request';
  }
  return 'Something went wrong';
}

function ErrorBanner({ message }) {
  if (!message) return null;
  return (
    <div style={{ padding: 10, background: 'rgba(139,46,46,0.08)', border: '1px solid rgba(139,46,46,0.2)', borderRadius: 4, color: 'var(--crimson)', fontSize: 13 }}>
      {message}
    </div>
  );
}

// ── Login ───────────────────────────────────────────────────────────────────
export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password, mfaRequired ? mfaCode : undefined);
      const routes = { owner: '/dashboard', agent: '/dashboard', buyer: '/dashboard', pro: '/pro', admin: '/admin' };
      navigate(routes[user.role] || '/dashboard');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Backend signals: an MFA code is required to complete this login.
      if (detail === 'mfa_code_required') {
        setMfaRequired(true);
        setError('Enter the 6-digit code from your authenticator app.');
      } else if (detail === 'mfa_enrollment_required') {
        // Admin who hasn't enrolled MFA yet — redirect via a short-lived
        // enrollment ticket. The /mfa/enroll endpoint requires an access
        // token, so we pass the credentials through history.state.
        navigate('/mfa/enroll', { state: { email, password } });
      } else if (detail === 'Invalid MFA code') {
        setMfaCode('');
        setError('Invalid code. Try again.');
      } else {
        setError(extractError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageShell>
        <BrandHeader subtitle="Sign in to your Property Redbook" />

        <div className="panel" style={{ padding: 28 }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <ErrorBanner message={error} />
            <div className="field">
              <label>Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" disabled={mfaRequired} />
            </div>
            <div className="field">
              <label>Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" disabled={mfaRequired} />
              <div style={{ marginTop: 6, textAlign: 'right' }}>
                <Link to="/forgot-password" style={{ fontSize: 12, color: 'var(--forest)' }}>Forgot password?</Link>
              </div>
            </div>
            {mfaRequired && (
              <div className="field">
                <label>Authenticator code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  autoFocus
                  placeholder="123456"
                  style={{ letterSpacing: '0.25em', textAlign: 'center', fontFamily: 'var(--mono)', fontSize: 18 }}
                />
                <div style={{ marginTop: 6, fontSize: 12, color: 'var(--ink-4)' }}>
                  Enter the 6-digit code from your authenticator app (Authy, Google Authenticator, 1Password).
                </div>
              </div>
            )}
            <button type="submit" className="btn primary lg" style={{ width: '100%', justifyContent: 'center' }} disabled={loading || (mfaRequired && mfaCode.length !== 6)}>
              {loading ? 'Signing in…' : (mfaRequired ? 'Verify' : 'Sign in')}
            </button>
            {mfaRequired && (
              <button
                type="button"
                className="btn"
                onClick={() => { setMfaRequired(false); setMfaCode(''); setError(''); }}
                style={{ width: '100%', justifyContent: 'center' }}
              >
                ← Back
              </button>
            )}
          </form>
        </div>

        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: 'var(--ink-4)' }}>
          Don't have an account? <Link to="/register" style={{ color: 'var(--forest)', fontWeight: 500 }}>Create one</Link>
        </div>
    </AuthPageShell>
  );
}

// ── Forgot Password ─────────────────────────────────────────────────────────
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authAPI.forgotPassword(email);
      setSubmitted(true);
    } catch (err) {
      setError(extractError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageShell>
        <BrandHeader subtitle="Reset your password" />

        <div className="panel" style={{ padding: 28 }}>
          {submitted ? (
            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 32 }}>✉️</div>
              <div style={{ fontWeight: 500 }}>Check your inbox</div>
              <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>
                If an account exists for <strong>{email}</strong>, we've sent a password reset link. It expires in 60 minutes.
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <ErrorBanner message={error} />
              <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>
                Enter your email address and we'll send you a link to reset your password.
              </div>
              <div className="field">
                <label>Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" />
              </div>
              <button type="submit" className="btn primary lg" style={{ width: '100%', justifyContent: 'center' }} disabled={loading}>
                {loading ? 'Sending…' : 'Send reset link'}
              </button>
            </form>
          )}
        </div>

        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: 'var(--ink-4)' }}>
          <Link to="/login" style={{ color: 'var(--forest)', fontWeight: 500 }}>Back to sign in</Link>
        </div>
    </AuthPageShell>
  );
}

// ── Reset Password ──────────────────────────────────────────────────────────
export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(extractError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthPageShell>
          <BrandHeader subtitle="Reset your password" />
          <div className="panel" style={{ padding: 28, textAlign: 'center', color: 'var(--crimson)', fontSize: 13 }}>
            Invalid reset link. <Link to="/forgot-password" style={{ color: 'var(--forest)' }}>Request a new one.</Link>
          </div>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell>
        <BrandHeader subtitle="Choose a new password" />

        <div className="panel" style={{ padding: 28 }}>
          {done ? (
            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 32 }}>✓</div>
              <div style={{ fontWeight: 500 }}>Password updated</div>
              <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>Redirecting you to sign in…</div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <ErrorBanner message={error} />
              <div className="field">
                <label>New password</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} placeholder="At least 8 characters" />
              </div>
              <div className="field">
                <label>Confirm password</label>
                <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} placeholder="••••••••" />
              </div>
              <button type="submit" className="btn primary lg" style={{ width: '100%', justifyContent: 'center' }} disabled={loading}>
                {loading ? 'Saving…' : 'Set new password'}
              </button>
            </form>
          )}
        </div>

        {!done && (
          <div style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: 'var(--ink-4)' }}>
            <Link to="/login" style={{ color: 'var(--forest)', fontWeight: 500 }}>Back to sign in</Link>
          </div>
        )}
    </AuthPageShell>
  );
}

// ── Common AU trades ─────────────────────────────────────────────────────────
const AU_TRADES = [
  'Architect',
  'Building Designer',
  'Building Inspector',
  'Builder / General Contractor',
  'Carpenter & Joiner',
  'Civil Engineer',
  'Conveyancer',
  'Electrician',
  'Glazier',
  'HVAC Technician',
  'Landscaper',
  'Land Surveyor',
  'Mason / Bricklayer',
  'Painter & Decorator',
  'Pest Inspector',
  'Plasterer',
  'Plumber',
  'Project Manager',
  'Property Manager',
  'Quantity Surveyor',
  'Roofer',
  'Solicitor / Lawyer',
  'Structural Engineer',
  'Tiler',
  'Town Planner',
  'Waterproofer',
  'Other',
];

// ── Validation helpers ────────────────────────────────────────────────────────
function validateAbn(val) {
  if (!val) return null; // optional
  const digits = val.replace(/\s/g, '');
  if (!/^\d+$/.test(digits)) return 'ABN/ACN must contain digits only';
  if (digits.length === 9) return null;  // valid ACN
  if (digits.length === 11) return null; // valid ABN
  return 'Enter a valid ABN (11 digits) or ACN (9 digits)';
}

function validatePhone(val) {
  if (!val) return null; // optional
  const digits = val.replace(/\s/g, '');
  if (!/^\d{10}$/.test(digits)) return 'Mobile number must be 10 digits (e.g. 0412 345 678)';
  return null;
}

// ── Complete Pro Profile Modal (step 2 of pro registration) ─────────────────
function CompleteProProfileModal({ onComplete, onSkip }) {
  const [form, setForm] = useState({
    bio: '',
    secondary_trade: '',
    license_number: '',
    suburb: '',
    state: '',
    service_areas: '',
    rate_band: '',
    response_time: '',
    is_available: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      await marketplaceAPI.updateMyProfile({
        bio: form.bio.trim() || undefined,
        secondary_trade: form.secondary_trade.trim() || undefined,
        license_number: form.license_number.trim() || undefined,
        suburb: form.suburb.trim() || undefined,
        state: form.state.trim() || undefined,
        service_areas: form.service_areas.split(',').map((s) => s.trim()).filter(Boolean),
        rate_band: form.rate_band || undefined,
        response_time: form.response_time || undefined,
        is_available: form.is_available,
      });
      onComplete();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not save profile. You can update it later.');
      setSaving(false);
    }
  }

  const fs = {
    width: '100%', boxSizing: 'border-box', height: 36, padding: '0 10px',
    border: '1px solid var(--hairline)', borderRadius: 6,
    background: 'var(--card)', fontSize: 13, fontFamily: 'inherit',
  };
  const lbl = (t) => (
    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5 }}>{t}</div>
  );

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 580 }}>
        {/* Header */}
        <div className="modal-head" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
          <div className="mono" style={{ fontSize: 10, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
            Step 2 of 2
          </div>
          <h2 className="serif" style={{ margin: 0, fontSize: 22, color: 'var(--forest)', fontWeight: 600 }}>
            Complete your profile
          </h2>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-4)', lineHeight: 1.5 }}>
            Help clients find and trust you. All fields are optional — you can update these any time from your profile page.
          </p>
        </div>

        {/* Body */}
        <div className="modal-body" style={{ display: 'grid', gap: 16 }}>
          {error && (
            <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, fontSize: 13, color: '#dc2626' }}>
              {error}
            </div>
          )}

          <div className="field">
            {lbl('Bio / About')}
            <textarea
              value={form.bio}
              onChange={set('bio')}
              rows={3}
              placeholder="Describe your experience, specialties, and what makes you stand out…"
              style={{ ...fs, height: 80, padding: '8px 10px', resize: 'vertical' }}
            />
          </div>

          <div className="r-cols-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="field">
              {lbl('Secondary trade')}
              <select style={fs} value={form.secondary_trade} onChange={set('secondary_trade')}>
                <option value="">— none —</option>
                {AU_TRADES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="field">
              {lbl('Licence number')}
              <input style={fs} value={form.license_number} onChange={set('license_number')} placeholder="e.g. BLD-NSW-12345" />
            </div>
          </div>

          <div className="r-cols-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="field">
              {lbl('Suburb')}
              <input style={fs} value={form.suburb} onChange={set('suburb')} placeholder="e.g. Surry Hills" />
            </div>
            <div className="field">
              {lbl('State')}
              <select style={fs} value={form.state} onChange={set('state')}>
                <option value="">— select —</option>
                {['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="field">
            {lbl('Service areas (comma-separated)')}
            <input style={fs} value={form.service_areas} onChange={set('service_areas')} placeholder="e.g. Inner Sydney, North Shore, Eastern Suburbs" />
          </div>

          <div className="r-cols-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="field">
              {lbl('Rate band')}
              <select style={fs} value={form.rate_band} onChange={set('rate_band')}>
                <option value="">— select —</option>
                <option value="$">$ — Budget-friendly</option>
                <option value="$$">$$ — Mid-range</option>
                <option value="$$$">$$$ — Premium</option>
              </select>
            </div>
            <div className="field">
              {lbl('Typical response time')}
              <select style={fs} value={form.response_time} onChange={set('response_time')}>
                <option value="">— select —</option>
                <option value="Same day">Same day</option>
                <option value="1 hour">Within 1 hour</option>
                <option value="2 hours">Within 2 hours</option>
                <option value="4 hours">Within 4 hours</option>
                <option value="24 hours">Within 24 hours</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              type="checkbox"
              id="is_available"
              checked={form.is_available}
              onChange={(e) => setForm((f) => ({ ...f, is_available: e.target.checked }))}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            <label htmlFor="is_available" style={{ fontSize: 13, color: 'var(--ink)', cursor: 'pointer' }}>
              I am currently available for new jobs
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-foot" style={{ justifyContent: 'space-between' }}>
          <button
            type="button"
            onClick={onSkip}
            style={{ background: 'none', border: 'none', fontSize: 13, color: 'var(--ink-4)', cursor: 'pointer', padding: '4px 0' }}
          >
            Skip for now →
          </button>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="btn ghost" onClick={onSkip}>Cancel</button>
            <button type="button" className="btn primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Complete profile'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ── Register ────────────────────────────────────────────────────────────────
export function RegisterPage() {
  const [form, setForm] = useState({
    email: '', password: '', first_name: '', last_name: '',
    role: 'owner', business_name: '', trade: '', phone: '', abn: '', website: '',
    consent_accepted: false,
  });
  const [step, setStep] = useState(1); // 1 = account, 2 = complete pro profile
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.role === 'pro') {
      const phoneErr = validatePhone(form.phone);
      if (phoneErr) { setError(phoneErr); return; }
      const abnErr = validateAbn(form.abn);
      if (abnErr) { setError(abnErr); return; }
    }

    setLoading(true);
    try {
      await register({ ...form, consent_version: CONSENT_VERSION });
      if (form.role === 'pro') {
        setStep(2);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(extractError(err));
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    { id: 'owner', label: 'Property Owner', desc: 'Track your property history' },
    { id: 'agent', label: 'Real Estate Agent', desc: 'Prepare listings & share reports' },
    { id: 'buyer', label: 'Buyer', desc: 'Review property condition' },
    { id: 'pro', label: 'Service Professional', desc: 'Win work, manage jobs' },
  ];

  return (
    <AuthPageShell maxWidth={520}>
      {/* Step 2: profile completion modal (overlays the page) */}
      {step === 2 && (
        <CompleteProProfileModal
          onComplete={() => navigate('/dashboard')}
          onSkip={() => navigate('/dashboard')}
        />
      )}
        <BrandHeader subtitle="Create your account" />

        {/* Step indicator for pros */}
        {form.role === 'pro' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 20 }}>
            {['Account details', 'Complete profile'].map((label, i) => (
              <React.Fragment key={i}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: '50%', fontSize: 11, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: step > i ? 'var(--forest)' : step === i + 1 ? 'var(--forest)' : 'var(--hairline)',
                    color: step >= i + 1 ? '#fff' : 'var(--ink-4)',
                  }}>{i + 1}</div>
                  <span style={{ fontSize: 12, color: step === i + 1 ? 'var(--forest)' : 'var(--ink-4)', fontWeight: step === i + 1 ? 600 : 400 }}>
                    {label}
                  </span>
                </div>
                {i === 0 && <div style={{ flex: 1, height: 1, background: 'var(--hairline)', margin: '0 10px' }} />}
              </React.Fragment>
            ))}
          </div>
        )}

        <div className="panel" style={{ padding: 28 }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <ErrorBanner message={error} />

            {/* Role selector */}
            <div className="field">
              <label>I am a…</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {roles.map((r) => (
                  <button key={r.id} type="button" onClick={() => setForm({ ...form, role: r.id })}
                    style={{
                      padding: 12, textAlign: 'left', borderRadius: 6, cursor: 'pointer',
                      border: `1px solid ${form.role === r.id ? 'var(--forest)' : 'var(--hairline)'}`,
                      background: form.role === r.id ? 'rgba(31,58,46,0.06)' : 'var(--card)',
                    }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: form.role === r.id ? 'var(--forest)' : 'var(--ink)' }}>{r.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>{r.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="r-cols-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="field">
                <label>First name</label>
                <input value={form.first_name} onChange={update('first_name')} required />
              </div>
              <div className="field">
                <label>Last name</label>
                <input value={form.last_name} onChange={update('last_name')} required />
              </div>
            </div>

            <div className="field">
              <label>Email</label>
              <input type="email" value={form.email} onChange={update('email')} required />
            </div>
            <div className="field">
              <label>Password</label>
              <input type="password" value={form.password} onChange={update('password')} required minLength={8} />
              <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>
                Min 8 characters — uppercase, lowercase, number and a special character (!@#$%…)
              </div>
            </div>

            {form.role === 'pro' && (
              <>
                <div style={{ margin: '4px 0', padding: '10px 14px', background: 'rgba(31,58,46,0.05)', borderRadius: 6, borderLeft: '3px solid var(--forest)' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--forest)', marginBottom: 2 }}>Professional account</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-4)', lineHeight: 1.5 }}>
                    After creating your account you can complete your profile (bio, location, rates, etc.) to appear in the marketplace.
                  </div>
                </div>

                <div className="r-cols-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="field">
                    <label>Business name *</label>
                    <input value={form.business_name} onChange={update('business_name')} required placeholder="e.g. Smith Plumbing" />
                  </div>
                  <div className="field">
                    <label>Trade *</label>
                    <select value={form.trade} onChange={update('trade')} required style={{ width: '100%', height: 38, padding: '0 10px', border: '1px solid var(--hairline)', borderRadius: 6, background: 'var(--card)', fontSize: 13, fontFamily: 'inherit' }}>
                      <option value="">— select trade —</option>
                      {AU_TRADES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>

                <div className="r-cols-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="field">
                    <label>Mobile number *</label>
                    <input value={form.phone} onChange={update('phone')} required placeholder="e.g. 0412 345 678" maxLength={10} inputMode="numeric" />
                  </div>
                  <div className="field">
                    <label>ABN (11 digits) or ACN (9 digits) *</label>
                    <input value={form.abn} onChange={update('abn')} required placeholder="e.g. 12345678901" maxLength={11} inputMode="numeric" />
                  </div>
                </div>

                <div className="field">
                  <label>Website (optional)</label>
                  <input type="url" value={form.website} onChange={update('website')} placeholder="https://yourbusiness.com.au" />
                </div>
              </>
            )}

            {/* Privacy / Terms consent — required */}
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', fontSize: 13, color: 'var(--ink-2)' }}>
              <input
                type="checkbox"
                required
                checked={form.consent_accepted}
                onChange={e => setForm({ ...form, consent_accepted: e.target.checked })}
                style={{ marginTop: 3, flexShrink: 0 }}
              />
              <span>
                I agree to the{' '}
                <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--forest)', fontWeight: 500 }}>Privacy Policy</a>
                {' '}and{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--forest)', fontWeight: 500 }}>Terms of Service</a>.
                {' '}<span style={{ color: 'var(--crimson)' }}>*</span>
              </span>
            </label>

            <button type="submit" className="btn primary lg" style={{ width: '100%', justifyContent: 'center' }} disabled={loading}>
              {loading ? 'Creating account…' : form.role === 'pro' ? 'Create account & continue →' : 'Create account'}
            </button>
          </form>
        </div>

        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: 'var(--ink-4)' }}>
          Already have an account? <Link to="/login" style={{ color: 'var(--forest)', fontWeight: 500 }}>Sign in</Link>
        </div>
    </AuthPageShell>
  );
}
